import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import LinkIconButton from '@atlaskit/button/icon/link';
import LinkButton from '@atlaskit/button/link';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/utils/teams-app-navigation/navigate-to-teams-app';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';

import { type PersonProfileData } from '../../types';
import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-preview.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile',
	},
	sendMessageLabel: {
		id: 'user-profile-preview.action-bar.send-message-label',
		defaultMessage: 'Message',
		description: 'Accessible label for the send message button',
	},
	viewOrgChartLabel: {
		id: 'user-profile-preview.action-bar.view-org-chart-label',
		defaultMessage: 'Reporting line',
		description: 'Accessible label for the org chart icon button',
	},
});

interface ActionBarProps {
	profileData: PersonProfileData;
	showOrgChart?: boolean;
	onViewOrgChart?: (accountId: string) => void;
	cloudId: string;
}

export function ActionBar({
	profileData,
	showOrgChart = true,
	onViewOrgChart,
	cloudId,
}: ActionBarProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { accountId, thirdPartyIntegrations, email } = profileData;
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			fireEvent('ui.userProfilePreview.clicked.thirdPartyMessage', {
				integration: externalMessage?.entry.integrationId ?? '',
				isAuxClick: event.type === 'auxclick',
			});
		},
		[fireEvent, externalMessage],
	);

	const handleOrgChartClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			fireEvent('ui.userProfilePreview.clicked.reportingLines', {
				isAuxClick: event.type === 'auxclick',
			});
			onViewOrgChart?.(accountId);
		},
		[fireEvent, onViewOrgChart, accountId],
	);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			const auxClick = event.type === 'auxclick';
			fireEvent('ui.userProfilePreview.clicked.viewProfile', { isAuxClick: auxClick });
			// A middle-click on a link opens a new tab natively, so only run SPA navigation for
			// primary clicks.
			if (!auxClick) {
				onNavigate?.(event);
			}
		},
		[fireEvent, onNavigate],
	);

	// TODO update to use the TeamsLinkIconButton (once available)
	return (
		<Inline space="space.100" alignBlock="center">
			{externalMessage && (
				<LinkIconButton
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					onAuxClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel)}
					isTooltipDisabled={false}
				/>
			)}
			{accountId && showOrgChart && onViewOrgChart && (
				<IconButton
					onClick={handleOrgChartClick}
					onAuxClick={handleOrgChartClick}
					icon={DepartmentIcon}
					label={intl.formatMessage(messages.viewOrgChartLabel)}
					isTooltipDisabled={false}
				/>
			)}
			{accountId && href && (
				<LinkButton
					href={href}
					appearance="default"
					target={target}
					onClick={handleViewProfileClick}
					onAuxClick={handleViewProfileClick}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
		</Inline>
	);
}
